if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.ifMediaStack = this.hasAttribute("data-media-stack");
        // Optionswerte der gewählten Variante (für Bildgruppen "###Tag + Tag | Beschreibung")
        this.currentOptions = null;
        try {
          const raw = this.getAttribute("data-variant-options");
          if (raw) this.currentOptions = JSON.parse(raw);
        } catch (e) {
          this.currentOptions = null;
        }
        this.lastVariantMediaId = parseInt(
          this.querySelector(".is-current-variant-media").dataset.mediaId,
        ); // 当前选择的产品变体对应的MediaId

        if (!this.thumbnails) return;
        this.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((thumbnail) => {
            thumbnail.addEventListener(
              "click",
              this.onThumbnailClick.bind(this),
            );
          });

        this.addEventListener("sliderChanged", this.onSlideChanged.bind(this));
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      /**
       * 媒体切换事件
       * @param event
       */
      onSlideChanged(event) {
        if (!event || !event.detail) return;

        const mediaId = event.detail.currentElement.dataset.mediaId;

        // 滚动缩略图到当前媒体
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return;

        if (!thumbnail.querySelector(".thumbnail-media").hasAttribute("aria-current")) {
          this.setActiveThumbnail(thumbnail);
        }
        if (this.thumbnails.initSliderStatus)
          this.thumbnails.moveElementToOptimalPosition(thumbnail); // 将缩略图元素移动到合适的位置
      }

      /**
       * 点击缩略图处理
       */
      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        this.setActiveThumbnail(thumbnail);

        this.changeMedia(thumbnail.getAttribute("data-target"));
      }

      /**
       * Bilder eines Slides mit hoher Priorität und ohne Lazy-Loading laden.
       */
      prioritizeMedia(item) {
        if (!item) return;
        item.querySelectorAll("img").forEach((img) => {
          // Fallback für JS-Lazy-Bilder (data-srcset), z. B. wenn ein Theme-Snippet sie noch so ausgibt
          if (img.hasAttribute("data-srcset") && typeof loadingImage === "function") {
            loadingImage(img);
          }
          img.loading = "eager";
          img.setAttribute("fetchpriority", "high");
        });
      }

      /**
       * Gruppierte Slides/Thumbnails (nur echte, keine Klone) einmal bestimmen und merken.
       */
      get groupedSlides() {
        if (!this._groupedSlides)
          this._groupedSlides = Array.from(
            this.viewer.querySelectorAll(
              "[data-media-group]:not(.slider-slide--clone)",
            ),
          );
        return this._groupedSlides;
      }

      get groupedThumbnails() {
        if (!this._groupedThumbnails)
          this._groupedThumbnails = this.thumbnails
            ? Array.from(
                this.thumbnails.querySelectorAll(
                  "[data-thumbnail-group]:not(.slider-slide--clone)",
                ),
              )
            : [];
        return this._groupedThumbnails;
      }

      /**
       * Sichtbarkeits-Klassen der Gruppen an die Variante anpassen.
       * Schreibt nur Elemente, deren Zustand sich wirklich ändert.
       * @returns {boolean} true, wenn sich die sichtbare Menge geändert hat
       */
      _syncGroupClasses(fallbackGroup) {
        const cache = new Map();
        const visible = (group) => {
          let result = cache.get(group);
          if (result === undefined) {
            result = this.groupVisible(group, fallbackGroup);
            cache.set(group, result);
          }
          return result;
        };

        // Erst alles lesen ...
        const toggles = [];
        for (const el of this.groupedSlides) {
          const want = visible(el.dataset.mediaGroup);
          if (el.classList.contains("is-current-variant-media-group") !== want)
            toggles.push([el, "is-current-variant-media-group", want]);
        }
        for (const el of this.groupedThumbnails) {
          const want = visible(el.dataset.thumbnailGroup);
          if (
            el.classList.contains("is-current-variant-thumbnail-group") !== want
          )
            toggles.push([el, "is-current-variant-thumbnail-group", want]);
        }
        // ... dann gebündelt schreiben.
        for (const [el, cls, want] of toggles) el.classList.toggle(cls, want);

        return toggles.length > 0;
      }

      /**
       * Schritt 1 des Galerie-Updates (günstig): Klassen für aktuelles Medium und Gruppen setzen.
       * Entscheidet, ob Viewer/Thumbnails neu installiert werden müssen (nur wenn sich die
       * sichtbare Menge ändert). Merkt offene Arbeit (_viewerDirty/_thumbsDirty/_pendingFinish),
       * damit ein abgebrochener (veralteter) Lauf vom nächsten Aufruf nachgeholt wird.
       * @returns {{needsReinstall: boolean}|null} null = nichts zu tun
       */
      _applyMediaState(currentVariantMediaId, variantOptions) {
        if (currentVariantMediaId == null) return null;
        const optionsKey = Array.isArray(variantOptions)
          ? variantOptions.join("\u0001")
          : null;
        const optionsChanged =
          optionsKey !== null &&
          optionsKey !==
            (Array.isArray(this.currentOptions)
              ? this.currentOptions.join("\u0001")
              : null);
        const dirty =
          this._viewerDirty || this._thumbsDirty || this._pendingFinish != null;
        // Gleiches Medium und gleiche Optionen: nichts zu tun.
        // Gleiches Medium, andere Optionen (z. B. Filzfarbe): Gruppen trotzdem neu bewerten.
        if (
          currentVariantMediaId === this.lastVariantMediaId &&
          !optionsChanged &&
          !dirty
        )
          return null;

        const previousMediaId = this.lastVariantMediaId;
        const mediaChanged = currentVariantMediaId !== previousMediaId;
        this.lastVariantMediaId = currentVariantMediaId;
        if (Array.isArray(variantOptions)) this.currentOptions = variantOptions;

        if (mediaChanged) {
          // 修改当前变体关联的媒体
          this.viewer
            .querySelectorAll(".is-current-variant-media")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media"),
            );
          this.viewer
            .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
            .forEach((element) =>
              element.classList.add("is-current-variant-media"),
            );

          // Hauptbild der neuen Variante sofort laden (kein Lazy/Pixel-Platzhalter).
          this.prioritizeMedia(
            this.viewer.querySelector(
              `.product-media-item[data-media-id="${currentVariantMediaId}"]`,
            ),
          );

          if (this.thumbnails) {
            this.thumbnails
              .querySelector(".is-current-variant-thumbnail")
              ?.classList.remove("is-current-variant-thumbnail");
            this.thumbnails
              .querySelector(`[data-target="${currentVariantMediaId}"]`)
              ?.classList.add("is-current-variant-thumbnail");
          }
        }

        // 如果开启了隐藏其它变体设置
        if (this.hasAttribute("data-hide-variants")) {
          const fallbackGroup = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          )?.dataset.mediaGroup;
          const groupsChanged = this._syncGroupClasses(fallbackGroup);

          // Varianten-Bilder sind nur sichtbar, solange sie das aktuelle Medium sind
          // (.product-media-item--variant:not(.is-current-variant-media)).
          // Ein Wechsel von/zu einem solchen Bild ändert die sichtbare Menge ebenfalls.
          let variantFlip = false;
          if (mediaChanged) {
            const isVariantItem = (id) =>
              id != null &&
              this.viewer.querySelector(
                `.product-media-item--variant[data-media-id="${id}"]`,
              ) !== null;
            variantFlip =
              isVariantItem(previousMediaId) ||
              isVariantItem(currentVariantMediaId);
          }

          // Reinstall NUR bei tatsächlich geänderter sichtbarer Menge
          if ((groupsChanged || variantFlip) && !this.ifMediaStack) {
            this._viewerDirty = true;
            this._thumbsDirty = true;
          }
        }

        const needsReinstall =
          !this.ifMediaStack && (this._viewerDirty || this._thumbsDirty);
        // Zum Slide springen nur bei neuem Medium oder nach Reinstall (Slider steht dann neu).
        // Gleiches Medium ohne Reinstall (z. B. nur Größe gewechselt): Position nicht anfassen.
        const jump = mediaChanged || needsReinstall || this._pendingFinish != null;
        this._pendingFinish = jump ? currentVariantMediaId : null;
        return { needsReinstall, jump };
      }

      /**
       * Variantengebundene Galerie (Produkte mit vielen Medien): fehlende Slides/Thumbnails der gewählten
       * Variante aus der Medienliste nachladen, bevor die Gruppen-Klassen gesetzt werden.
       * lxryEnsure wird vom Inline-Skript in snippets/product-media-gallery gesetzt.
       */
      _ensureMedia(currentVariantMediaId, variantOptions) {
        if (typeof this.lxryEnsure === "function") {
          this.lxryEnsure(variantOptions, currentVariantMediaId);
        }
      }

      _reinstallViewer() {
        if (this._viewerDirty && this.viewer) this.viewer.reInstall();
        this._viewerDirty = false;
      }

      _reinstallThumbnails() {
        if (this._thumbsDirty && this.thumbnails) this.thumbnails.reInstall();
        this._thumbsDirty = false;
      }

      /**
       * Letzter Schritt: zum Slide springen (ohne Reinstall bei unveränderter Gruppe).
       */
      _finishMediaChange(currentVariantMediaId) {
        this._pendingFinish = null;
        this.changeMedia(currentVariantMediaId);

        // 手机端，当媒体切换的时候，返回页面顶部
        if (isMobileScreen()) {
          scrollElementToHeaderBottom(this, 16);
        }
      }

      /**
       * 更新相册（synchron). Reinstall der Slider nur, wenn sich die Media-Gruppe /
       * die sichtbare Menge tatsächlich ändert.
       * @param currentVariantMediaId Featured Media Id der Variante
       * @param variantOptions Optionswerte der Variante (für Gruppen "###Tag + Tag")
       */
      updateGallery(currentVariantMediaId, variantOptions) {
        this._ensureMedia(currentVariantMediaId, variantOptions);
        const step = this._applyMediaState(
          currentVariantMediaId,
          variantOptions,
        );
        if (!step) return;

        if (step.needsReinstall) {
          this._reinstallViewer();
          this._reinstallThumbnails();
        }
        if (step.jump) this._finishMediaChange(currentVariantMediaId);
      }

      /**
       * Wie updateGallery, aber in kurzen Tasks (Klassen → Viewer → Thumbnails → Sprung),
       * damit der Browser dazwischen malen/Eingaben verarbeiten kann.
       * @param options.yieldFn gibt den Hauptthread frei (Promise)
       * @param options.isStale true, wenn ein neuerer Klick den Lauf ablöst
       */
      async updateGalleryStaged(
        currentVariantMediaId,
        variantOptions,
        { yieldFn, isStale } = {},
      ) {
        this._ensureMedia(currentVariantMediaId, variantOptions);
        const step = this._applyMediaState(
          currentVariantMediaId,
          variantOptions,
        );
        if (!step) return;

        if (step.needsReinstall) {
          if (yieldFn) await yieldFn();
          // Abgelöst: offene Arbeit bleibt über _viewerDirty/_thumbsDirty/_pendingFinish
          // vermerkt und wird vom nächsten Aufruf erledigt.
          if (isStale && isStale()) return;
          this._reinstallViewer();

          if (this._thumbsDirty) {
            if (yieldFn) await yieldFn();
            if (isStale && isStale()) return;
            this._reinstallThumbnails();
          }
          if (yieldFn) await yieldFn();
          if (isStale && isStale()) return;
        }
        if (step.jump) this._finishMediaChange(currentVariantMediaId);
      }

      /**
       * Gehört ein Medium mit Gruppe `group` zur aktuellen Variante?
       * group = "Tag" oder "Tag + Tag"; jeder Tag muss ein Optionswert der Variante sein.
       * Ohne bekannte Optionen (alte Aufrufer): Gruppe des Featured Media der Variante (`fallbackGroup`).
       */
      groupVisible(group, fallbackGroup) {
        if (!group) return true;
        if (Array.isArray(this.currentOptions)) {
          const tags = group
            .split("+")
            .map((tag) => tag.trim())
            .filter(Boolean);
          return (
            tags.length > 0 &&
            tags.every((tag) => this.currentOptions.includes(tag))
          );
        }
        return group === fallbackGroup;
      }

      /**
       * 设置活跃媒体
       * @param mediaId
       */
      changeMedia(mediaId) {
        const activeMedia = this.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.ifMediaStack) {
          // 如果是 Stack 布局，需要将当前变体 Featured Media 放置最前面
          activeMedia.parentElement.prepend(activeMedia);
        } else {
          // 轮播模式，切换当前轮播
          if (this.viewer.initSliderStatus)
            this.viewer.slideByElement(activeMedia);
        }
      }

      /**
       * 设置活跃缩略图
       * @param thumbnail
       */
      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);
      }
    }
  );
}
